import React, { useState } from 'react';
import { X, Upload, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import favicon from "../assets/favicon_new.png";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000"; // Backend URL

export default function CreateTemplate({ onClose, isDarkMode, themeClasses, onTemplateCreated }) {
  const [step, setStep] = useState(1); // 1: Upload, 2: Processing, 3: Form
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    extractFields: [],
    instructions: ''
  });
  const [newField, setNewField] = useState('');
  const [error, setError] = useState(null);

  const parseJsonResponse = (jsonString) => {
    try {
      // Remove code fences if present
      let cleanedJson = jsonString.trim();
      if (cleanedJson.startsWith('```')) {
        const regex = /^```(?:\s*json\s*)?\n?([\s\S]*?)\n?```$/m;
        const match = cleanedJson.match(regex);
        if (match) {
          cleanedJson = match[1].trim();
        }
      }
      return JSON.parse(cleanedJson);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      throw new Error('Failed to parse document structure');
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setStep(2);
      
      try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const token = localStorage.getItem('authToken');
        const response = await axios.post('/api/sync/detect_document', formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        });

        // Parse the JSON response
        const parsedData = parseJsonResponse(response.data.result);
        
        // Update form with detected fields and type
        setFormData(prev => ({
          ...prev,
          description: parsedData.type || '',
          extractFields: parsedData.fields || []
        }));

        setError(null); // Clear any existing errors
        setStep(3);
      } catch (error) {
        console.error('Error detecting document:', error);
        setError(error.response?.data?.message || 'Failed to process document');
        setStep(1);
      }
    }
  };

  const skipToForm = () => {
    setStep(3);
    setError(null);
  };

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (newField.trim()) {
        setFormData(prev => ({
          ...prev,
          extractFields: [...prev.extractFields, newField.trim()]
        }));
        setNewField('');
      }
    }
  };

  const removeField = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      extractFields: prev.extractFields.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('extractFields', JSON.stringify(formData.extractFields));
      formDataToSend.append('instructions', formData.instructions);

      const token = localStorage.getItem('authToken');
      await axios.post('/api/templates', formDataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onTemplateCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create template');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`
        relative w-full max-w-2xl rounded-2xl shadow-2xl
        ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
        p-6
        max-h-[90vh]
        overflow-hidden
        flex flex-col
      `}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {step === 2 && (
              <button
                onClick={() => setStep(prev => prev - 1)}
                className={`mr-4 p-2 rounded-lg transition-colors
                  ${isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                  }`}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className={`text-xl font-semibold ${themeClasses.text}`}>
              {step === 1 && 'AI document detector'}
              {step === 2 && 'Processing Document'}
              {step === 3 && 'Template Details'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors
              ${isDarkMode 
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-600'
              }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-2">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="upload"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6 pb-6"
              >
                <label className={`
                  flex flex-col items-center justify-center w-full h-64
                  border-2 border-dashed rounded-xl cursor-pointer
                  transition-colors duration-300
                  ${isDarkMode
                    ? 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                  }
                `}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className={`w-12 h-12 mb-4 ${themeClasses.subtext}`} />
                    <p className={`mb-2 text-sm ${themeClasses.text}`}>
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className={`text-xs ${themeClasses.subtext}`}>
                      PDF, DOCX, or any image format
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf,.docx,image/*"
                  />
                </label>

                <div className="flex justify-center mt-6">
                  <button
                    onClick={skipToForm}
                    className={`
                      px-4 py-2 rounded-lg text-sm
                      ${isDarkMode
                        ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                      }
                      transition-colors duration-300
                    `}
                  >
                    Skip document detection
                  </button>
                </div>

                {error && (
                  <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 mt-4">
                    {error}
                  </div>
                )}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="processing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6 pb-6"
              >
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="relative">
                    
                    <div className="relative w-16 h-16 mb-5">
  <motion.div
    className="w-full h-full"
    animate={{ rotate: 360 }}
    transition={{
      duration: 2,
      ease: "linear",
      repeat: Infinity,
    }}
  >
    <img 
      src={favicon} 
      alt="Loading" 
      className="w-full h-full object-contain" 
      onError={(e) => {
        console.log('Image failed to load:', e.target.src);
        e.target.style.display = 'none';
      }}
    />
  </motion.div>
</div>

                  </div>
                  <motion.p
                    className={`text-lg font-semibold ${themeClasses.text} mt-6`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                  >
                    Analyzing document...
                  </motion.p>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6 pb-6"
              >
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`
                        w-full px-3 py-2 rounded-lg
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:ring-2 focus:ring-blue-500
                      `}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows="3"
                      className={`
                        w-full px-3 py-2 rounded-lg
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:ring-2 focus:ring-blue-500
                      `}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                      Extract Fields
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.extractFields.map((field, index) => (
                        <span
                          key={index}
                          className={`
                            inline-flex items-center px-3 py-1 rounded-full text-sm
                            ${isDarkMode
                              ? 'bg-gray-700 text-gray-200'
                              : 'bg-gray-100 text-gray-800'
                            }
                          `}
                        >
                          {field}
                          <button
                            onClick={() => removeField(index)}
                            className="ml-2 text-gray-500 hover:text-gray-700"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={newField}
                      onChange={e => setNewField(e.target.value)}
                      onKeyDown={handleFieldKeyDown}
                      placeholder="Type and press Enter or comma to add fields"
                      className={`
                        w-full px-3 py-2 rounded-lg
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:ring-2 focus:ring-blue-500
                      `}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                      Instructions
                    </label>
                    <textarea
                      value={formData.instructions}
                      onChange={e => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                      rows="4"
                      className={`
                        w-full px-3 py-2 rounded-lg
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:ring-2 focus:ring-blue-500
                      `}
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400">
                    {error}
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    onClick={onClose}
                    className={`
                      px-4 py-2 rounded-lg
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }
                    `}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                  >
                    Create Template
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
} 